import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, getAllQueues } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiUserCheck, FiUserX, FiArrowRight } from 'react-icons/fi';
import { CRM_PERMISSIONS, PERM_LABEL } from '../crm/perms';

// Batch 1 A.4: user management is a FULL PAGE (was a modal). The editor replaces
// the list in place rather than overlaying it, so a background refresh can never
// unmount it mid-edit. It edits the full name and the four CRM permissions as
// labelled checkboxes (admins have them all implicitly, shown as "الكل").
//
// UI polish (Batch 1): the editor is contained (max-width, centred) and grouped
// into cards — account data, CRM permissions, allowed queues — with a sticky
// action bar. Styling only; no logic, field names, or save payload changed.

const emptyForm = {
  username: '', password: '', fullName: '', extension: '', role: 'agent',
  queueNames: [], crmPermissions: []
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(false);   // full-page editor open?
  const [editingUser, setEditingUser] = useState(null);
  const [allQueues, setAllQueues] = useState({ queues: [], ivrs: [] });
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchAllQueues();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchAllQueues = async () => {
    try {
      const res = await getAllQueues();
      const queues = res.data.filter(q => q.type === 'queue');
      const ivrs = res.data.filter(q => q.type === 'ivr');
      setAllQueues({ queues, ivrs });
    } catch (error) {
      console.error('Failed to fetch queues:', error);
    }
  };

  const parseList = (val) => {
    if (Array.isArray(val)) return val;
    if (!val) return [];
    return val.split(',').map(s => s.trim()).filter(Boolean);
  };

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setError('');
    setEditing(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      password: '',
      fullName: user.fullName,
      extension: user.extension,
      role: user.role,
      queueNames: parseList(user.queueNames),
      crmPermissions: parseList(user.crmPermissions)
    });
    setError('');
    setEditing(true);
  };

  const closeEditor = () => {
    setEditing(false);
    setEditingUser(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const data = {
        ...form,
        queueNames: form.queueNames.join(','),
        crmPermissions: form.crmPermissions
      };
      if (!data.password && editingUser) delete data.password;

      if (editingUser) {
        await updateUser(editingUser.id, data);
      } else {
        if (!data.password) {
          setError('كلمة المرور مطلوبة');
          return;
        }
        await createUser(data);
      }
      closeEditor();
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'حدث خطأ');
    }
  };

  const handleToggleActive = async (user) => {
    if (window.confirm(`هل تريد ${user.isActive ? 'تعطيل' : 'تفعيل'} حساب ${user.fullName}؟`)) {
      await updateUser(user.id, { isActive: !user.isActive });
      fetchUsers();
    }
  };

  const handleDelete = async (user) => {
    if (window.confirm(`هل أنت متأكد من حذف ${user.fullName}؟ هذا الإجراء لا يمكن التراجع عنه.`)) {
      await deleteUser(user.id);
      fetchUsers();
    }
  };

  const toggleQueue = (name) => {
    setForm(prev => ({
      ...prev,
      queueNames: prev.queueNames.includes(name)
        ? prev.queueNames.filter(q => q !== name)
        : [...prev.queueNames, name]
    }));
  };

  const togglePerm = (perm) => {
    setForm(prev => ({
      ...prev,
      crmPermissions: prev.crmPermissions.includes(perm)
        ? prev.crmPermissions.filter(p => p !== perm)
        : [...prev.crmPermissions, perm]
    }));
  };

  // ==========================================================================
  // FULL-PAGE EDITOR
  // ==========================================================================
  if (editing) {
    return (
      // Batch 7 (Group B): calm scope. Same editor, same cards, same fields.
      <div className="crm-calm crm-calm-page">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-outline btn-sm" onClick={closeEditor}>
              <FiArrowRight /> رجوع للقائمة
            </button>
            <h1 className="page-title" style={{ margin: 0 }}>
              {editingUser ? `تعديل: ${editingUser.fullName}` : 'إضافة مستخدم جديد'}
            </h1>
          </div>
        </div>

        <form className="usr-editor" onSubmit={handleSubmit}>
          {error && <div className="crm-notice err">{error}</div>}

          {/* ---- account data ---- */}
          <div className="usr-card">
            <div className="usr-card-title">بيانات الحساب</div>
            <div className="usr-grid2">
              <div className="usr-field full">
                <label>الاسم الكامل</label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  required
                />
              </div>
              <div className="usr-field">
                <label>اسم المستخدم</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  required
                  disabled={!!editingUser}
                />
              </div>
              <div className="usr-field">
                <label>التحويلة في 3CX</label>
                <input
                  type="text"
                  value={form.extension}
                  onChange={(e) => setForm({ ...form, extension: e.target.value })}
                  placeholder="مثال: 101"
                  required
                />
              </div>
              <div className="usr-field">
                <label>{editingUser ? 'كلمة المرور الجديدة' : 'كلمة المرور'}</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required={!editingUser}
                />
                {editingUser && <div className="usr-hint">اتركها فارغة للإبقاء على كلمة المرور الحالية</div>}
              </div>
              <div className="usr-field">
                <label>الدور</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="agent">موظف</option>
                  <option value="admin">مدير</option>
                </select>
              </div>
            </div>
          </div>

          {/* ---- CRM permissions ---- */}
          <div className="usr-card">
            <div className="usr-card-title">صلاحيات الـ CRM</div>
            {form.role === 'admin' ? (
              <span className="usr-admin-badge">الكل — المدير يملك جميع الصلاحيات تلقائياً</span>
            ) : (
              <div className="usr-perms">
                {CRM_PERMISSIONS.map((perm) => {
                  const on = form.crmPermissions.includes(perm);
                  return (
                    <label key={perm} className={`usr-perm${on ? ' on' : ''}`}>
                      <input type="checkbox" checked={on} onChange={() => togglePerm(perm)} />
                      <span className="txt">
                        <span className="lbl">{PERM_LABEL[perm]}</span>
                        <span className="key">{perm}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* ---- allowed queues (agents only) ---- */}
          {form.role === 'agent' && (
            <div className="usr-card">
              <div className="usr-card-title">الكيوهات المسموحة ({form.queueNames.length} مختارة)</div>
              <div className="usr-queuebox">
                <div className="usr-qhead real">✓ الكيوهات الحقيقية ({allQueues.queues.length})</div>
                {allQueues.queues.map(q => {
                  const isSelected = form.queueNames.includes(q.name);
                  return (
                    <label key={q.name} className={`usr-qrow${isSelected ? ' on' : ''}`}>
                      <div className="left">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleQueue(q.name)} />
                        <span style={{ fontSize: 14, fontWeight: isSelected ? 600 : 400 }}>{q.name}</span>
                      </div>
                      <span className="usr-qcount">{q.calls} مكالمة</span>
                    </label>
                  );
                })}
                <div className="usr-qhead ivr">ⓘ رسائل الترحيب والتحويلات ({allQueues.ivrs.length})</div>
                {allQueues.ivrs.map(q => {
                  const isSelected = form.queueNames.includes(q.name);
                  return (
                    <label key={q.name} className={`usr-qrow${isSelected ? ' on' : ''}`}>
                      <div className="left">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleQueue(q.name)} />
                        <span style={{ fontSize: 14, fontWeight: isSelected ? 600 : 400 }}>{q.name}</span>
                      </div>
                      <span className="usr-qcount">{q.calls} مكالمة</span>
                    </label>
                  );
                })}
              </div>
              <div className={`usr-notice ${form.queueNames.length === 0 ? 'warn' : 'ok'}`}>
                {form.queueNames.length === 0
                  ? '⚠️ بدون كيوهات مختارة، الموظف لن يتمكن من رؤية أي مكالمات'
                  : `✓ الموظف سيرى فقط مكالمات الـ ${form.queueNames.length} كيو المختارة`}
              </div>
            </div>
          )}

          {/* ---- sticky action bar ---- */}
          <div className="usr-actions">
            <button type="submit" className="btn btn-primary" style={{ minWidth: 160 }}>
              {editingUser ? 'حفظ التعديلات' : 'إضافة المستخدم'}
            </button>
            <button type="button" className="btn btn-outline" onClick={closeEditor}>رجوع للقائمة</button>
          </div>
        </form>
      </div>
    );
  }

  // ==========================================================================
  // LIST
  // ==========================================================================
  return (
    <div className="crm-calm crm-calm-page">
      <div className="page-header">
        <h1 className="page-title">إدارة المستخدمين</h1>
        <button className="btn btn-primary" onClick={openCreate}>
          <FiPlus /> إضافة مستخدم
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>الاسم</th>
              <th>اسم المستخدم</th>
              <th>التحويلة</th>
              <th>الدور</th>
              <th>الصلاحيات</th>
              <th>الكيوهات</th>
              <th>الحالة</th>
              <th>إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => {
              const userQueues = parseList(user.queueNames);
              const userPerms = parseList(user.crmPermissions);
              return (
                <tr key={user.id}>
                  <td style={{ fontWeight: 600 }}>{user.fullName}</td>
                  <td>{user.username}</td>
                  <td style={{ direction: 'ltr', textAlign: 'right' }}>{user.extension}</td>
                  <td>
                    <span className={`badge ${user.role === 'admin' ? 'badge-callback-answered' : 'badge-retry'}`}>
                      {user.role === 'admin' ? 'مدير' : 'موظف'}
                    </span>
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    {user.role === 'admin' ? (
                      <span className="crm-muted" style={{ fontSize: 12 }}>الكل (مدير)</span>
                    ) : userPerms.length === 0 ? (
                      <span className="crm-faint" style={{ fontSize: 12 }}>لا توجد</span>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {userPerms.map(p => (
                          <span key={p} className="crm-pill crm-pill-accent">{PERM_LABEL[p] || p}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    {user.role === 'admin' ? (
                      <span className="crm-muted" style={{ fontSize: 12 }}>الكل (مدير)</span>
                    ) : userQueues.length === 0 ? (
                      <span className="crm-txt-danger" style={{ fontSize: 12 }}>لم يتم التحديد</span>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {userQueues.map(q => (
                          <span key={q} className="crm-pill crm-pill-info">{q}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${user.isActive ? 'badge-answered' : 'badge-missed'}`}>
                      {user.isActive ? 'فعال' : 'معطل'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(user)} title="تعديل">
                        <FiEdit2 size={12} />
                      </button>
                      <button
                        className={`btn btn-sm ${user.isActive ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => handleToggleActive(user)}
                        title={user.isActive ? 'تعطيل' : 'تفعيل'}
                      >
                        {user.isActive ? <FiUserX size={12} /> : <FiUserCheck size={12} />}
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(user)} title="حذف">
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersPage;
